import type { ExternalNode } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/external_node_pb";
import { Timestamp } from "@bufbuild/protobuf";
import { OrgApiKeyService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/org_api_key_connect";
import { addDays, endOfDay } from "date-fns";
import { productNameToDisplayName } from "components/NodeDetail/BrevConnect/shared";
import { bytesToGBString } from "components/Environment/RegisteredCompute/utils";
import { createConnectClient } from "server/connect";
import { toast } from "utils/toast";

export const APPLICATION_WORKSPACE_SCROLL_CONTAINER_ID =
  "application-workspace";
export const DEFAULT_API_KEY_NAME = "Brev Connect";
export const DEFAULT_KEY_EXPIRATION_DAYS = 7;
export const MAX_KEY_EXPIRATION_DAYS = 90;
export const MAX_COMPUTE_NAME_LENGTH = 63;
const API_KEY_WRITE_ROLE = "role:key:write";
const INSTALL_CLI_COMMAND =
  'bash -c "$(curl -fsSL https://raw.githubusercontent.com/brevdev/brev-cli/main/bin/install-latest.sh)"';
const VALID_COMPUTE_NAME = /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/;

export type Phase = "waiting" | "success";
export type ComputeNodeNetworkMode = "isolated" | "meshed";
export type CreatedAPIKey = { id: string; name: string };

export type AddedComputeNode = {
  externalNodeId: string;
  name: string;
  deviceModel: string;
  productName?: "DGX Spark" | "DGX Station";
  productImageSrc?: string;
  gpu: string;
  cpuCores: string;
  memory: string;
  architecture: string;
  meshDnsName: string;
  meshIp: string;
};

export type BrevConnectRouteParams = {
  orgId: string;
};

export const resetBrevConnectScrollPosition = () => {
  try {
    window.scrollTo(0, 0);
  } catch {
    // Some test environments define scrollTo but do not implement it.
  }

  const scrollTargets = [
    document.scrollingElement,
    document.documentElement,
    document.body,
    document.getElementById(APPLICATION_WORKSPACE_SCROLL_CONTAINER_ID),
  ].filter((target): target is Element => target !== null);

  scrollTargets.forEach((target) => {
    try {
      target.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {
      // Direct scrollTop/scrollLeft assignment below still handles the reset.
    }

    target.scrollTop = 0;
    target.scrollLeft = 0;
  });
};

export const toAddedComputeNode = (node: ExternalNode): AddedComputeNode => {
  const displayProductName = productNameToDisplayName(
    node.nodeSpec?.productName,
  );
  const productName =
    displayProductName === "DGX Spark" || displayProductName === "DGX Station"
      ? displayProductName
      : undefined;
  const gpu = node.nodeSpec?.gpus
    .map(({ count, model }) => `${count}× ${model}`)
    .join(", ");

  return {
    externalNodeId: node.externalNodeId,
    name: node.name || node.externalNodeId,
    deviceModel: productName ? `NVIDIA ${productName}` : "Linux machine",
    productName,
    productImageSrc:
      productName === "DGX Spark"
        ? "/assets/spark.svg"
        : productName === "DGX Station"
          ? "/assets/station.svg"
          : undefined,
    gpu: gpu || "—",
    cpuCores:
      node.nodeSpec?.cpuCount === undefined
        ? "—"
        : String(node.nodeSpec.cpuCount),
    memory: node.nodeSpec?.ramBytes
      ? bytesToGBString(node.nodeSpec.ramBytes)
      : "—",
    architecture: node.nodeSpec?.architecture || "—",
    meshDnsName: node.connectivityInfo?.meshDnsName || "—",
    meshIp: node.connectivityInfo?.meshIpAddress || "—",
  };
};

const orgAPIKeyClient = createConnectClient(OrgApiKeyService);

const shellEscape = (value: string) => `'${value.replace(/'/g, "'\\''")}'`;

export const isBrevAPIKey = (value: string) => value.trim().startsWith("bak-");

export const validateComputeName = (
  value: string,
  externalNodeNames?: Set<string>,
) => {
  const name = value.trim();
  if (!name) return "Compute name is required.";
  if (name.length > MAX_COMPUTE_NAME_LENGTH) {
    return `Name must be ${MAX_COMPUTE_NAME_LENGTH} characters or fewer.`;
  }
  if (!VALID_COMPUTE_NAME.test(name)) {
    return "Name must start with a letter or digit and contain only letters, digits, hyphens, underscores, and dots.";
  }
  return externalNodeNames?.has(name.toLowerCase())
    ? "A compute node with this name already exists."
    : "";
};

export const defaultAPIKeyName = (computeName: string) =>
  computeName.trim()
    ? `${DEFAULT_API_KEY_NAME}: ${computeName.trim()}`
    : DEFAULT_API_KEY_NAME;

export const defaultKeyExpirationDate = () =>
  endOfDay(addDays(new Date(), DEFAULT_KEY_EXPIRATION_DAYS));

export const maximumKeyExpirationDate = () =>
  endOfDay(addDays(new Date(), MAX_KEY_EXPIRATION_DAYS));

export const buildRegisterComputeCommand = (
  computeName: string,
  apiKey: string,
  registrationToken: string,
) =>
  `${INSTALL_CLI_COMMAND} && export BREV_API_KEY=${shellEscape(apiKey.trim())} && "$HOME/.local/bin/brev" register --name ${shellEscape(computeName.trim())} --registration-token ${shellEscape(registrationToken)}`;

const apiKeyErrorMessage = (error: unknown) =>
  error instanceof Error && error.message
    ? error.message
    : "Failed to create a new API key.";

const notifyCleanupFailure = (name: string) => {
  toast.error("Unused API key is still active", {
    description: `Automatic cleanup failed. Delete “${name}” from API Keys.`,
  });
};

const deleteUnusedAPIKey = async ({ id, name }: CreatedAPIKey) => {
  try {
    await orgAPIKeyClient.deleteOrgAPIKey({ id });
    return true;
  } catch (error) {
    console.error("Failed to clean up an unused API key", error);
    notifyCleanupFailure(name);
    return false;
  }
};

export const createBrevAPIKey = async ({
  expirationDate,
  name,
  orgId,
}: {
  expirationDate: Date;
  name: string;
  orgId: string;
}) => {
  const response = await orgAPIKeyClient.createOrgAPIKey({
    name,
    roleIds: [API_KEY_WRITE_ROLE],
    expiresAt: Timestamp.fromDate(expirationDate),
    orgId,
  });

  if (!response.apiKey?.id) {
    throw new Error(
      `Brev may have created “${name}” but did not return its ID. Delete the key from API Keys before trying again.`,
    );
  }

  if (!isBrevAPIKey(response.plaintextValue)) {
    const wasDeleted = await deleteUnusedAPIKey({
      id: response.apiKey.id,
      name,
    });

    throw new Error(
      wasDeleted
        ? "Brev did not return a valid API key."
        : "Brev created an API key but did not return a valid value. Delete the key from API Keys before trying again.",
    );
  }

  return response.plaintextValue;
};

export { apiKeyErrorMessage, endOfDay };
